import { chakra, Flex, Link, Spinner, Table, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useEventLogAgentUsers } from '~/queries/eventLogAgentUsers'
import { useGetUserAgentEventLogs } from '~/queries/queueQueries'
import { DateRange } from '~/types/Shared'

import { formatDateString, formatTimeString } from '@mangovoice/core'

import { useDashboardContext } from '../context/DashboardContext'

const Thead = Table.Header
const Tbody = Table.Body
const Tr = Table.Row
const Th = Table.ColumnHeader
const Td = Table.Cell

const stickyHeaderStyles = {
  position: 'sticky' as const,
  top: '0',
  zIndex: '2',
  bg: 'bg.panel',
  _after: {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '0.1rem',
    backgroundColor: 'bg.emphasized',
  },
}

const StickyTh = chakra(Th, {
  base: {
    ...stickyHeaderStyles,
    padding: '0.75rem 1rem 0.75rem 0rem',
  },
})

const tableHeader = (title: string) => (
  <StickyTh>
    <Text
      textTransform="none"
      fontSize={'0.75rem'}
      fontWeight={'700'}
      lineHeight={'1rem'}
      color={'var(--chakra-colors-fg-default)'}
    >
      {title}
    </Text>
  </StickyTh>
)

const cellText = (text: string) => {
  return (
    <Text
      overflow={'hidden'}
      textOverflow={'ellipsis'}
      whiteSpace={'nowrap'}
      fontSize={'1rem'}
      fontWeight={'400'}
      lineHeight={'1.5rem'}
      color={'var(--chakra-colors-fg-default)'}
    >
      {text}
    </Text>
  )
}

const cell = (children: React.ReactNode) => {
  return (
    <Td
      padding={'0'}
      height={'3.75rem'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'flex-start'}
      flex={'1 0 0'}
    >
      {children}
    </Td>
  )
}

const textCell = (text: string) => {
  return cell(cellText(text))
}

const formatStartTime = (timestamp: string): string => {
  return `${formatDateString(timestamp)} ${formatTimeString(timestamp)}`
}

interface EventLogTableProps {
  dateTimeRange: DateRange
  agentUserId?: string
}

const EventLogTable = ({ dateTimeRange, agentUserId }: EventLogTableProps) => {
  const { t } = useTranslation()
  const { pbxId } = useDashboardContext()
  const agentUsers = useEventLogAgentUsers()

  const {
    data: eventLogs,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isLoading,
  } = useGetUserAgentEventLogs({
    params: {
      pbx_id: pbxId,
      start_date: dateTimeRange.after?.toISOString().split('T')[0],
      end_date: dateTimeRange.before?.toISOString().split('T')[0],
      user_uuid: agentUserId,
    },
  })

  // Infinite scroll makes the row count unbounded, so resolve names via a
  // lookup map instead of a per-row linear scan of pbxUsers.
  const usersById = useMemo(
    () => new Map(agentUsers.map((user) => [user.id, user])),
    [agentUsers],
  )

  const isEmpty =
    !isLoading &&
    !isFetchingNextPage &&
    (eventLogs?.pages?.every((page) => page.results.length === 0) ?? false)

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleScroll = () => {
    if (!scrollContainerRef.current || isFetchingNextPage || !hasNextPage)
      return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    // Check if user has scrolled to the bottom (with a small threshold)
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      fetchNextPage()
    }
  }

  // Keep loading while the loaded rows don't fill the scroll area: onScroll
  // can't fire when there's nothing to scroll, so a short page would otherwise
  // strand the rest of the day's events unloaded. Bail on a page error so a
  // failing endpoint doesn't spin here (the errored page never appends, so the
  // viewport stays under-filled and the effect would otherwise re-fire).
  useEffect(() => {
    const container = scrollContainerRef.current
    if (
      !container ||
      isFetchingNextPage ||
      isFetchNextPageError ||
      !hasNextPage
    ) {
      return
    }
    if (container.scrollHeight <= container.clientHeight) {
      fetchNextPage()
    }
  }, [
    eventLogs,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  ])

  return (
    <Flex
      padding="1.25rem"
      backgroundColor="var(--chakra-colors-bg-panel)"
      alignSelf="stretch"
      flex="1"
      minH="0"
      overflow="hidden"
      flexDirection="column"
    >
      <Table.Root
        variant="line"
        style={{ tableLayout: 'fixed', width: '100%' }}
      >
        <Thead>
          <Tr>
            {tableHeader(t('eventLogTable.when'))}
            {tableHeader(t('eventLogTable.who'))}
            {tableHeader(t('eventLogTable.status'))}
            {tableHeader(t('eventLogTable.inQueue'))}
            {tableHeader(t('eventLogTable.changedBy'))}
          </Tr>
        </Thead>
      </Table.Root>
      <Flex
        overflowY="auto"
        flex="1"
        minH="0"
        alignItems="flex-start"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <Table.Root
          variant="line"
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <Tbody>
            {eventLogs?.pages?.map((page) =>
              page.results.map((event) => (
                <Tr
                  key={event.id}
                  height={'4.75rem'}
                  borderBottom={
                    '0.1rem solid var(--chakra-colors-border-subtle)'
                  }
                  alignSelf={'stretch'}
                >
                  {textCell(formatStartTime(event.created_at))}
                  {textCell(
                    usersById.get(event.user_uuid ?? '')?.full_name ??
                      t('eventLogTable.unknown'),
                  )}
                  {textCell(event.agent_status)}
                  {textCell(
                    event.logged_in
                      ? t('eventLogTable.yes')
                      : t('eventLogTable.no'),
                  )}
                  {textCell(
                    event.initiated_by_user_uuid
                      ? event.initiated_by_name || t('eventLogTable.unknown')
                      : t('eventLogTable.system'),
                  )}
                </Tr>
              )),
            )}
            {isFetchingNextPage && (
              <Tr>
                <Td colSpan={5} padding="1rem">
                  <Flex justifyContent="center">
                    <Spinner size="sm" />
                  </Flex>
                </Td>
              </Tr>
            )}
            {isFetchNextPageError && !isFetchingNextPage && (
              <Tr>
                <Td colSpan={5} padding="1rem">
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    gap="0.5rem"
                  >
                    <Text color="var(--chakra-colors-fg-muted)">
                      {t('eventLogTable.failedToLoad')}
                    </Text>
                    <Link as="button" onClick={() => fetchNextPage()}>
                      {t('eventLogTable.retry')}
                    </Link>
                  </Flex>
                </Td>
              </Tr>
            )}
            {isEmpty && (
              <Tr>
                <Td colSpan={5} padding="3rem 1rem">
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    gap="0.25rem"
                  >
                    <Text
                      fontWeight="700"
                      color="var(--chakra-colors-fg-default)"
                    >
                      {t('eventLogTable.noRecords')}
                    </Text>
                    <Text color="var(--chakra-colors-fg-muted)">
                      {t('eventLogTable.noRecordsHint')}
                    </Text>
                  </Flex>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table.Root>
      </Flex>
    </Flex>
  )
}

export default EventLogTable
